<script setup lang="ts">
/**
 * 业务组件展示 Tab
 * 展示 app/platform/design/widgets 目录下的所有组件
 */
import IconQuality from '~/design/icons/high-quality.svg?component';
import { getFeUrl } from '~/utils/url';

// Import widgets from platform/design/widgets (only those with index.vue)
import AdvantageGrids from '~/design/widgets/advantage-grids/index.vue';
import DigitalCases from '~/design/widgets/digital-cases/index.vue';
import ImageCarousel from '~/design/widgets/image-carousel/index.vue';
import ImageTextCarousel from '~/design/widgets/image-text-carousel/index.vue';
import MobileBottomUserPop from '~/design/widgets/mobile-bottom-user-pop/index.vue';
import MultiTabsVideo from '~/design/widgets/multi-tabs-video/index.vue';
import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import ThemeProvider from '~/design/widgets/theme-provider/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import VideoCarousel from '~/design/widgets/video-carousel/index.vue';

// Mock Data for DigitalCases
const digitalCasesData = [
  {
    brand: getFeUrl('/youyan-platform/main/logo2/zx.png'),
    trend: 'up',
    number: '200%',
    label: '视频制作效率',
    description: '我们用有言制作的精品课程，一经上线瞬间引起了公司(中兴)各业务单位的关注...',
    link: 'https://youyan3d.com',
    name: '中兴',
  },
  {
    brand: getFeUrl('/youyan-platform/main/logo2/skq.png'),
    trend: 'down',
    number: '98%',
    label: '视频制作成本',
    description: '有言让斯凯奇彻底告别‘一页纸培训’时代...',
    link: 'https://youyan3d.com',
    name: '斯凯奇',
  },
];

// Mock Data for ImageCarousel
const imageCarouselData = [
  { url: getFeUrl('/youyan-platform/scene/profession/1.jpg?x-oss-process=image/format,webp') },
  { url: getFeUrl('/youyan-platform/scene/profession/2.jpg?x-oss-process=image/format,webp') },
];

// Mock Data for ImageTextCarousel
const imageTextCarouselData = [
  {
    title: '高质量',
    url: getFeUrl('/youyan-platform/scene/profession/1.jpg?x-oss-process=image/format,webp'),
    desc: '从灯光、场景，到人物形象、表现和运镜均达到专业级视频水准。',
    list: [
      { label: '光影', content: '影视级布光' },
      { label: '细节', content: '发丝级渲染' },
    ],
  },
  {
    title: '高效率',
    url: getFeUrl('/youyan-platform/scene/profession/2.jpg?x-oss-process=image/format,webp'),
    desc: '分钟级视频生产，快速响应业务需求。',
    list: [
      { label: '速度', content: '10倍提升' },
      { label: '成本', content: '降低90%' },
    ],
  },
];

// Mock Data for VideoCarousel
const videoCarouselData = [
  {
    title: 'Tab 1',
    subTitle: '核心技术展示',
    url: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.mp4'),
    poster: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.png'),
    desc: '内置数千款超写实 3D 数字人角色，覆盖不同年龄、族裔与风格。',
    tabDesc: '3D 角色',
  },
  {
    title: 'Tab 2',
    subTitle: '场景构建能力',
    url: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.mp4'), // Reusing video
    poster: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.png'),
    desc: '基于强大的3D场景构建能力，提供拥有逼真光影的背景。',
    tabDesc: '3D 场景',
  },
];

// Mock Data for MultiTabsVideo
const multiTabsVideoData = [
  {
    id: 'tab1',
    title: '数字人',
    desc: '超写实数字人',
    videoUrl: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.mp4'),
    image: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.png'),
  },
  {
    id: 'tab2',
    title: '声音',
    desc: '高质量语音合成',
    videoUrl: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.mp4'),
    image: getFeUrl('/youyan-platform/api-intro/core-techs/core-tech-1.png'),
  },
];

// Mock Data for AdvantageGrids
const advantageGridsCards = [
  { icon: IconQuality, title: '极致画质', desc: '影视级4K渲染' },
  { icon: IconQuality, title: '快速生成', desc: '分钟级出片速度' },
  { icon: IconQuality, title: '简单易用', desc: 'PPT式操作体验' },
];

const handleStart = () => {
  console.log('Start button clicked');
};
</script>

<template>
  <div class="widgets-tab">
    <div class="widgets-tab__header">
      <h1>业务组件</h1>
      <p class="widgets-tab__desc">
        展示 <code>app/platform/design/widgets</code> 目录下的所有业务组件
      </p>
    </div>

    <div class="widgets-tab__content">
      <!-- Top Header -->
      <div class="demo-block">
        <h3>Top Header - 顶部导航</h3>
        <p class="demo-desc">页面顶部导航栏组件</p>
        <div class="demo-preview">
          <TopHeader />
        </div>
        <div class="code-preview">
          <pre><code>&lt;TopHeader /&gt;</code></pre>
        </div>
      </div>

      <div class="demo-block">
        <h3>Digital Cases - 数字案例</h3>
        <p class="demo-desc">展示数字化案例的组件</p>
        <div class="demo-preview">
          <DigitalCases :dataCases="digitalCasesData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;DigitalCases :dataCases="digitalCasesData" /&gt;</code></pre>
        </div>
      </div>

      <!-- Image Carousel -->
      <div class="demo-block">
        <h3>Image Carousel - 图片轮播</h3>
        <p class="demo-desc">图片轮播展示组件</p>
        <div class="demo-preview">
          <ImageCarousel :infos="imageCarouselData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;ImageCarousel :infos="imageCarouselData" /&gt;</code></pre>
        </div>
      </div>

      <!-- Image Text Carousel -->
      <div class="demo-block">
        <h3>Image Text Carousel - 图文轮播</h3>
        <p class="demo-desc">图文结合的轮播组件</p>
        <div class="demo-preview">
          <ImageTextCarousel :infos="imageTextCarouselData" showDots />
        </div>
        <div class="code-preview">
          <pre><code>&lt;ImageTextCarousel :infos="imageTextCarouselData" showDots /&gt;</code></pre>
        </div>
      </div>

      <!-- Video Carousel -->
      <div class="demo-block">
        <h3>Video Carousel - 视频轮播</h3>
        <p class="demo-desc">视频轮播展示组件</p>
        <div class="demo-preview">
          <VideoCarousel :infos="videoCarouselData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;VideoCarousel :infos="videoCarouselData" /&gt;</code></pre>
        </div>
      </div>

      <!-- Multi Tabs Video -->
      <div class="demo-block">
        <h3>Multi Tabs Video - 多标签视频</h3>
        <p class="demo-desc">多标签切换的视频展示组件</p>
        <div class="demo-preview">
          <MultiTabsVideo title="产品演示" desc="多维度展示产品特性" :tabs="multiTabsVideoData" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;MultiTabsVideo title="产品演示" :tabs="multiTabsVideoData" /&gt;</code></pre>
        </div>
      </div>

      <!-- Start Right Now -->
      <div class="demo-block">
        <h3>Start Right Now - 立即开始</h3>
        <p class="demo-desc">引导用户立即开始的行动号召组件</p>
        <div class="demo-preview">
          <StartRightNow :handler="handleStart" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;StartRightNow :handler="handleStart" /&gt;</code></pre>
        </div>
      </div>

      <!-- Advantage Grids -->
      <div class="demo-block">
        <h3>Advantage Grids - 优势网格</h3>
        <p class="demo-desc">网格布局展示产品优势</p>
        <div class="demo-preview">
          <AdvantageGrids :cards="advantageGridsCards" />
        </div>
        <div class="code-preview">
          <pre><code>&lt;AdvantageGrids :cards="advantageGridsCards" /&gt;</code></pre>
        </div>
      </div>

      <!-- Mobile Bottom User Pop -->
      <div class="demo-block">
        <h3>Mobile Bottom User Pop - 移动端用户弹窗</h3>
        <p class="demo-desc">移动端底部用户信息弹窗</p>
        <div class="demo-preview">
          <MobileBottomUserPop />
        </div>
        <div class="code-preview">
          <pre><code>&lt;MobileBottomUserPop /&gt;</code></pre>
        </div>
      </div>

      <!-- Theme Provider -->
      <div class="demo-block">
        <h3>Theme Provider - 主题提供者</h3>
        <p class="demo-desc">主题管理和切换组件</p>
        <div class="demo-preview">
          <ThemeProvider>
            <div style="padding: 20px; text-align: center; color: var(--x-text-1)">
              Theme Content
            </div>
          </ThemeProvider>
        </div>
        <div class="code-preview">
          <pre><code>&lt;ThemeProvider /&gt;</code></pre>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.widgets-tab {
  width: 100%;
  padding: 24px;

  &__header {
    margin-bottom: 32px;

    h1 {
      margin: 0 0 8px 0;
      font-size: 28px;
      font-weight: 700;
      color: var(--x-text-1);
    }
  }

  &__desc {
    margin: 0;
    font-size: 14px;
    color: var(--x-text-4);

    code {
      padding: 2px 6px;
      background: var(--x-back-3);
      border-radius: 4px;
      font-family: 'Monaco', 'Courier New', monospace;
      font-size: 13px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 1200px;
    margin: 0 auto;
  }
}

.demo-block {
  padding: 24px;
  background: var(--x-back-1);
  border: 1px solid var(--x-back-4);
  border-radius: 8px;

  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--x-text-1);
  }
}

.demo-desc {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--x-text-4);
}

.demo-preview {
  padding: 24px;
  background: var(--x-back-2);
  border-radius: 6px;
  margin-bottom: 16px;
}

.code-preview {
  pre {
    margin: 0;
    padding: 16px;
    background: var(--x-back-3);
    border-radius: 6px;
    overflow-x: auto;

    code {
      font-family: 'Monaco', 'Courier New', monospace;
      font-size: 13px;
      color: var(--x-text-2);
      line-height: 1.6;
    }
  }
}
</style>
